import { UploadFile } from 'antd/lib/upload/interface'
import { get } from 'lodash'
import Papa from 'papaparse'
import { Analyzer, DATA_TYPES as AnalyzerDATA_TYPES } from 'type-analyzer'
const ACCEPTED_ANALYZER_TYPES = [
  AnalyzerDATA_TYPES.DATE,
  AnalyzerDATA_TYPES.TIME,
  AnalyzerDATA_TYPES.DATETIME,
  AnalyzerDATA_TYPES.NUMBER,
  AnalyzerDATA_TYPES.INT,
  AnalyzerDATA_TYPES.FLOAT,
  AnalyzerDATA_TYPES.BOOLEAN,
  AnalyzerDATA_TYPES.STRING,
  AnalyzerDATA_TYPES.GEOMETRY,
  AnalyzerDATA_TYPES.GEOMETRY_FROM_STRING,
  AnalyzerDATA_TYPES.PAIR_GEOMETRY_FROM_STRING,
  AnalyzerDATA_TYPES.ZIPCODE,
  AnalyzerDATA_TYPES.ARRAY,
  AnalyzerDATA_TYPES.OBJECT,
]
const IGNORE_DATA_TYPES = Object.keys(AnalyzerDATA_TYPES).filter(
  (type) => !ACCEPTED_ANALYZER_TYPES.includes(type),
)

function renameDuplicateFields(fieldOrder: string[]): {
  allNames: string[]
  fieldByIndex: Record<number, string>
} {
  return fieldOrder.reduce(
    (
      accu: { allNames: string[]; fieldByIndex: Record<number, string> },
      field: string,
      i: number,
    ) => {
      const { allNames } = accu
      let fieldName = field

      if (allNames.includes(field)) {
        let counter = 0
        while (allNames.includes(`${field}-${counter}`)) {
          counter++
        }
        fieldName = `${field}-${counter}`
      }

      accu.fieldByIndex[i] = fieldName
      accu.allNames.push(fieldName)

      return accu
    },
    { allNames: [], fieldByIndex: {} },
  )
}

export const getFieldOptionsFromSourceData = (
  file: UploadFile,
  cb: (opts: string[], values: string[][]) => void,
): void => {
  const blob = file.originFileObj ?? (file as unknown as File)
  if (!blob || !(blob instanceof Blob)) {
    return
  }

  const reader = new FileReader()

  reader.readAsText(blob, 'UTF-8')
  reader.onload = function (evt) {
    const result = evt.target?.result as string | undefined

    if (result) {
      const parsedData = get(
        Papa.parse(result, { preview: 7, skipEmptyLines: true }),
        'data',
      ) as string[][] | undefined

      if (!parsedData || parsedData.length === 0) return
      const [fields, ...values] = parsedData
      if (!fields) return

      const sample: Record<string, string> = {}
      values[0]?.forEach((val: string, idx: number) => {
        const fieldName = fields[idx]
        if (fieldName != null) sample[fieldName] = val
      })
      const options: string[] = getFieldsFromData([sample], fields).map(
        (column: { name: string }) => `${column.name}`,
      )
      cb(options, values)
    }
  }
}

interface FieldMeta {
  key: string
  type?: string
  format?: string
}

interface FieldInfo {
  name: string
  format?: string
  tableFieldIndex: number
  type?: string
  analyzerType?: string
}

function getFieldsFromData(
  data: Record<string, string>[],
  fieldOrder: string[],
): FieldInfo[] {
  const metadata = Analyzer.computeColMeta(
    data,
    [{ regex: /.*geojson|all_points/g, dataType: 'GEOMETRY' }],
    { ignoredDataTypes: IGNORE_DATA_TYPES },
  ) as FieldMeta[]

  const { fieldByIndex } = renameDuplicateFields(fieldOrder)

  return fieldOrder.reduce(
    (orderedArray: FieldInfo[], field: string, index: number) => {
      const name = fieldByIndex[index] ?? field

      const fieldMeta = metadata.find((m: FieldMeta) => m.key === field)
      const { type, format } = fieldMeta || {}

      orderedArray[index] = {
        name,
        format,
        tableFieldIndex: index + 1,
        type: type,
        analyzerType: type,
      }
      return orderedArray
    },
    [],
  )
}

export const transformSourceType = (sourceType: string): string =>
  sourceType.replace(':', '')

export const retrievePureUrl = (rawUrl: string): string =>
  rawUrl.replace('https://www.', '').replace('https://', '')
export const extractUrl = (
  rawUrl: string | undefined,
  sourceUrl: string,
): string | undefined => {
  const pattern = retrievePureUrl(sourceUrl)
  return rawUrl && new RegExp(pattern).test(rawUrl)
    ? rawUrl.split(pattern)[1]
    : rawUrl
}
